package com.evoapps.evoshift.domain.usecase.billing

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.billing.BillingCustomer
import com.evoapps.evoshift.domain.billing.OwnerPlan
import com.evoapps.evoshift.domain.billing.PlanCheck
import com.evoapps.evoshift.domain.billing.PlanOffer
import com.evoapps.evoshift.domain.billing.PlanResource
import com.evoapps.evoshift.domain.billing.PlanLimits
import com.evoapps.evoshift.domain.billing.PlanTier
import com.evoapps.evoshift.domain.billing.WorkplacePlan
import com.evoapps.evoshift.domain.model.Owner
import com.evoapps.evoshift.domain.billing.Plans
import com.evoapps.evoshift.domain.repository.AuthRepository
import com.evoapps.evoshift.domain.repository.BillingRepository
import com.evoapps.evoshift.domain.repository.OwnerRepository
import com.evoapps.evoshift.domain.repository.PlanRepository
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.emitAll
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.transformLatest
import kotlinx.coroutines.withTimeoutOrNull
import kotlin.time.Instant

/**
 * The signed-in owner's plan (SH-38, SH-52): the trial from their owner profile, or a bought plan
 * from the store SDK's view merged with the Firestore mirror the RevenueCat webhook writes, so a
 * plan bought on one platform counts on all of them (the SDK alone knows only its own store; the
 * mirror alone lags a purchase by a few seconds). Identifies the store SDK as the signed-in uid
 * on the way, so only owner screens (which use this) register anyone with RevenueCat.
 */
class ObserveOwnerPlanUseCase(
    private val authRepository: AuthRepository,
    private val ownerRepository: OwnerRepository,
    private val billingRepository: BillingRepository,
    private val now: () -> Instant = { AppClock.now() }
) {
    @OptIn(ExperimentalCoroutinesApi::class)
    operator fun invoke(): Flow<OwnerPlan> = authRepository.observeCurrentUser()
        .map { it?.uid }
        .distinctUntilChanged()
        .flatMapLatest { uid ->
            if (uid == null) {
                flowOf(OwnerPlan.notStarted(billingRepository.isConfigured))
            } else {
                flow {
                    billingRepository.identify(uid)
                    val owner = ownerRepository.getOwner(uid).getOrNull()
                    val mirror = ownerRepository.observeBilling(uid).catch { emit(BillingCustomer()) }
                    emitAll(combine(billingRepository.customer, mirror) { sdk, mirrored ->
                        resolveOwnerPlan(uid, sdk, mirrored, owner, billingRepository.isConfigured, now())
                            .copy(billingStore = billingRepository.store)
                    })
                }
            }
        }
        .distinctUntilChanged()
}

/**
 * The effective plan of [uid]: a bought plan when either source has an active plan entitlement,
 * with the seats of Plans.resolvePlan (the SDK's entitlements win over the mirror's of the same id,
 * as they are newer; SDK data still belonging to a previous user is ignored), else the trial while
 * it runs, else none. Without an owner profile there is no business yet and the trial has not started.
 */
internal fun resolveOwnerPlan(
    uid: String,
    sdk: BillingCustomer,
    mirror: BillingCustomer,
    owner: Owner?,
    isBillingConfigured: Boolean,
    now: Instant
): OwnerPlan {
    val sdkIsCurrent = sdk.uid == uid
    val entitlements = (mirror.entitlements + if (sdkIsCurrent) sdk.entitlements else emptyList())
        .associateBy { it.id.trim().lowercase() }.values.toList()
    val subscriptionEnds = mirror.subscriptionEnds + if (sdkIsCurrent) sdk.subscriptionEnds else emptyMap()
    val premium = Plans.resolvePlan(entitlements, now, subscriptionEnds)
    // The function starts the trial a moment after the owner profile appears.
    val trialEndsAt = owner?.let { Plans.trialEndsAt(it.trialStartedAt ?: now) }
    val managementUrl = sdk.managementUrl.takeIf { sdkIsCurrent } ?: mirror.managementUrl
    return when {
        premium != null -> OwnerPlan(
            PlanTier.PREMIUM, premium.seats, trialEndsAt, premium.entitlement, isBillingConfigured, managementUrl,
            storePlan = Plans.resolveStorePlan(entitlements, now, subscriptionEnds)
        )
        trialEndsAt == null || now < trialEndsAt -> OwnerPlan(
            PlanTier.TRIAL, PlanLimits.UNLIMITED, trialEndsAt, null, isBillingConfigured, managementUrl
        )
        else -> OwnerPlan(PlanTier.NONE, 0, trialEndsAt, null, isBillingConfigured, managementUrl)
    }
}

/**
 * Whether the owner's plan allows one more workplace when there are [currentCount] already.
 * Allowed when the plan does not enforce limits (see Plans.ENFORCE_LIMITS_WITHOUT_BILLING) or
 * cannot be read in time, so a slow store never blocks work.
 */
class CheckPlanLimitUseCase(
    private val observeOwnerPlan: ObserveOwnerPlanUseCase,
    private val timeoutMillis: Long = 5_000
) {
    suspend operator fun invoke(resource: PlanResource, currentCount: Int): PlanCheck {
        val plan = withTimeoutOrNull(timeoutMillis) { observeOwnerPlan().first() } ?: return PlanCheck.Allowed
        if (!plan.enforcesLimits) return PlanCheck.Allowed
        val limit = plan.limits.of(resource)
        return if (currentCount < limit) PlanCheck.Allowed else PlanCheck.LimitReached(resource, limit, plan.tier)
    }
}

/** The owner's plan as the members of [workplaceId] see it; null until the function wrote it. */
class ObserveWorkplacePlanUseCase(private val planRepository: PlanRepository) {
    operator fun invoke(workplaceId: String): Flow<WorkplacePlan?> =
        planRepository.observeWorkplacePlan(workplaceId).catch { emit(null) }
}

/**
 * Whether [workplaceId]'s data may be changed (SH-65): false once its plan ended (tier NONE, or
 * the trial's or plan's end passed - also while the app stays open over that moment), which makes
 * the workplace read-only for everyone (Capability.withPlan). True while the plan is unknown (not
 * written yet, unreadable), without a workplace, and in builds that cannot sell a plan (see
 * Plans.ENFORCE_LIMITS_WITHOUT_BILLING): nobody is locked out by a slow read or a missing key.
 */
class ObservePlanActiveUseCase(
    private val planRepository: PlanRepository,
    private val billingRepository: BillingRepository,
    private val now: () -> Instant = { AppClock.now() }
) {
    @OptIn(ExperimentalCoroutinesApi::class)
    operator fun invoke(workplaceId: String?): Flow<Boolean> {
        if (workplaceId == null || (!billingRepository.isConfigured && !Plans.ENFORCE_LIMITS_WITHOUT_BILLING)) return flowOf(true)
        return planRepository.observeWorkplacePlan(workplaceId)
            .catch { emit(null) }
            .transformLatest { plan ->
                val at = now()
                val active = plan == null || plan.isActiveAt(at)
                emit(active)
                // Still running: it ends at activeUntil unless the function writes a renewal first.
                val until = plan?.activeUntil
                if (active && until != null) {
                    delay(until - at)
                    emit(false)
                }
            }
            .distinctUntilChanged()
    }

    /** Whether [workplaceId]'s plan allows changes now; true when it cannot be read in time. */
    suspend fun isActive(workplaceId: String?, timeoutMillis: Long = 5_000): Boolean =
        withTimeoutOrNull(timeoutMillis) { invoke(workplaceId).first() } ?: true
}

/**
 * How many seats the signed-in owner's team takes, across all their workplaces (from the plan of
 * one of them, which the function counts for all); null before the first workplace or until known.
 */
class ObserveSeatsInUseUseCase(
    private val authRepository: AuthRepository,
    private val ownerRepository: OwnerRepository,
    private val planRepository: PlanRepository
) {
    @OptIn(ExperimentalCoroutinesApi::class)
    operator fun invoke(): Flow<Int?> = authRepository.observeCurrentUser()
        .map { it?.uid }
        .distinctUntilChanged()
        .flatMapLatest { uid ->
            flow {
                val workplaceId = uid?.let { ownerRepository.getOwner(it).getOrNull() }?.workplaceIds?.firstOrNull()
                if (workplaceId == null) emit(null)
                else emitAll(planRepository.observeWorkplacePlan(workplaceId).map { it?.seatsUsed }.catch { emit(null) })
            }
        }
        .distinctUntilChanged()
}

/**
 * Whether [adding] more people may take a seat at [workplaceId] (inviting, adding or bringing
 * back someone), for owners and managers alike: from the workplace's plan, which counts the
 * people of all the owner's workplaces. Only an ended plan refuses ([PlanCheck.LimitReached]
 * with [PlanTier.NONE]); SH-220: growing past a seat tier never blocks, it is
 * [PlanCheck.OverPlan] and the person is saved (the owner is offered the next tier). Allowed
 * while that plan is unknown or not enforced in this build, so a slow read never blocks work.
 */
class CheckSeatsUseCase(
    private val planRepository: PlanRepository,
    private val billingRepository: BillingRepository,
    private val now: () -> Instant = { AppClock.now() },
    private val timeoutMillis: Long = 5_000
) {
    suspend operator fun invoke(workplaceId: String, adding: Int = 1): PlanCheck {
        if (!billingRepository.isConfigured && !Plans.ENFORCE_LIMITS_WITHOUT_BILLING) return PlanCheck.Allowed
        val plan = withTimeoutOrNull(timeoutMillis) {
            planRepository.observeWorkplacePlan(workplaceId).catch { emit(null) }.first()
        } ?: return PlanCheck.Allowed
        val tier = plan.tierAt(now())
        return when {
            tier == PlanTier.NONE -> PlanCheck.LimitReached(PlanResource.SEATS, 0, PlanTier.NONE)
            plan.seats != PlanLimits.UNLIMITED && plan.seatsUsed + adding > plan.seats ->
                PlanCheck.OverPlan(plan.seats, plan.seatsUsed + adding)
            else -> PlanCheck.Allowed
        }
    }

    /**
     * SH-214: how many more people fit at [workplaceId]: 0 without a plan, null when there is no
     * limit (the trial, an unlimited plan) or it is unknown or not enforced in this build.
     */
    suspend fun seatsLeft(workplaceId: String): Int? {
        if (!billingRepository.isConfigured && !Plans.ENFORCE_LIMITS_WITHOUT_BILLING) return null
        val plan = withTimeoutOrNull(timeoutMillis) {
            planRepository.observeWorkplacePlan(workplaceId).catch { emit(null) }.first()
        } ?: return null
        return when {
            plan.tierAt(now()) == PlanTier.NONE -> 0
            plan.seats == PlanLimits.UNLIMITED -> null
            else -> plan.seatsLeft
        }
    }
}

/**
 * The paywall's offers, the seat tiers of the RevenueCat offering: by seats, then in the order
 * of Plans.OFFERED_PERIODS (other periods after).
 */
class GetPlanOffersUseCase(private val billingRepository: BillingRepository) {
    suspend operator fun invoke(): Result<List<PlanOffer>> = billingRepository.getOffers().map { offers ->
        offers.sortedWith(
            compareBy<PlanOffer> { it.seats }
                .thenBy { offer -> Plans.OFFERED_PERIODS.indexOf(offer.period).let { if (it < 0) Int.MAX_VALUE else it } }
        )
    }
}

class PurchasePlanUseCase(private val billingRepository: BillingRepository) {
    suspend operator fun invoke(offer: PlanOffer): Result<Unit> = billingRepository.purchase(offer)
}

/**
 * Asks the store for purchases this store account already owns (e.g. after reinstalling, or a
 * plan bought before signing in on this device). True when the store now reports a paid plan.
 */
class RestorePurchasesUseCase(
    private val billingRepository: BillingRepository,
    private val now: () -> Instant = { AppClock.now() }
) {
    suspend operator fun invoke(): Result<Boolean> = billingRepository.restorePurchases().map {
        Plans.resolvePlan(billingRepository.customer.value.entitlements, now()) != null
    }
}

class ManageSubscriptionUseCase(private val billingRepository: BillingRepository) {
    suspend operator fun invoke(plan: OwnerPlan): Result<Unit> =
        billingRepository.manageSubscription(plan.managementUrl)
}

/** Logs the store SDK out when the person signs out, so the next account starts clean. */
class ResetBillingIdentityUseCase(private val billingRepository: BillingRepository) {
    suspend operator fun invoke() = billingRepository.identify(null)
}
